import acc from "accounting"
import { Flex, Modal, Radio, Typography } from "antd"
import { useTranslation } from "react-i18next"

import FallbackMarker from "../IconCell/FallbackMarker"

import styles from "./IconConflictDialog.module.less"

const { Text } = Typography

const NO_ICON = "none"

const formatCount = (value) => acc.formatNumber(value || 0, 0, ",", ".")

/**
 * Shown when saving a UI name would place two or more different icons in one group.
 *
 * This *replaces* the rename confirmation rather than following it — support never passes through
 * both dialogs. Nothing is pre-selected and Save stays disabled until an option is chosen, because
 * the system has no basis for guessing which brand mark is the right one. Each option carries the
 * raw value it comes from and that value's volume, which is what makes the choice informed.
 *
 * Cancelling changes nothing at all: the name is not saved, no icon moves, nothing is logged.
 */
const IconConflictDialog = ({
  open,
  record,
  conflict,
  selected,
  busy,
  onSelect,
  onSave,
  onCancel
}) => {
  const { t } = useTranslation()

  if (!record || !conflict) return null

  return (
    <Modal
      open={open}
      title={t("Which icon should the merged row use?")}
      okText={t("Save")}
      // Every modal gets a patterned background unless it opts out with clear-bg.
      classNames={{ content: "clear-bg" }}
      cancelText={t("Cancel")}
      okButtonProps={{ disabled: !selected, loading: busy }}
      onOk={onSave}
      onCancel={onCancel}
    >
      <Flex vertical gap={12}>
        <Text type="secondary">
          {t("These values will be shown to merchants as one row, so they need a single icon:")}
        </Text>

        <ul className={styles.values}>
          {conflict.raw_values.map((value) => (
            <li key={value}>
              <Text>{value}</Text>
            </li>
          ))}
        </ul>

        <Radio.Group
          value={selected}
          onChange={(event) => onSelect(event.target.value)}
          className={styles.options}
        >
          {conflict.icon_options.map((option) => (
            <Radio key={option.icon.id} value={option.icon.id} className={styles.option}>
              <Flex align="center" gap={10}>
                <img src={option.icon.url} alt="" className={styles.icon} />
                <div>
                  <Text>{option.raw_value}</Text>
                  <Text type="secondary" className={styles.count}>
                    {formatCount(option.transactions_count)} {t("transactions")}
                  </Text>
                </div>
              </Flex>
            </Radio>
          ))}

          <Radio value={NO_ICON} className={styles.option}>
            <Flex align="center" gap={10}>
              <FallbackMarker />
              {/* An explicit decision, not a side effect of not choosing. */}
              <Text>{t("No icon")}</Text>
            </Flex>
          </Radio>
        </Radio.Group>
      </Flex>
    </Modal>
  )
}

export default IconConflictDialog
